<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
    @click.self="$emit('close')"
  >
    <div class="bg-white rounded-lg w-full max-w-5xl p-6 my-8">
      <h3 class="font-semibold mb-4">{{ $t('smm.etsy.title') }}</h3>

      <div v-if="!preview" class="space-y-3">
        <div>
          <label class="text-sm text-gray-600">{{ $t('smm.media.shop') }}</label>
          <select v-model="shopId" required class="w-full border rounded-lg p-2 mt-1">
            <option value="">{{ $t('common.select_placeholder') }}</option>
            <option
              v-for="shop in shops"
              :key="shop.id"
              :value="String(shop.id)"
            >{{ shop.name }}</option>
          </select>
        </div>

        <div class="flex gap-2 text-sm">
          <button
            type="button"
            class="px-3 py-1 rounded-lg"
            :class="mode === 'url' ? 'bg-orange-100 text-orange-800' : 'text-gray-600 hover:bg-gray-100'"
            @click="mode = 'url'"
          >
            {{ $t('smm.etsy.tab_url') }}
          </button>
          <button
            type="button"
            class="px-3 py-1 rounded-lg"
            :class="mode === 'html' ? 'bg-orange-100 text-orange-800' : 'text-gray-600 hover:bg-gray-100'"
            @click="mode = 'html'"
          >
            {{ $t('smm.etsy.tab_html') }}
          </button>
        </div>

        <div>
          <label class="text-sm text-gray-600">{{ $t('smm.etsy.url_label') }}</label>
          <input
            v-model="url"
            type="url"
            required
            :placeholder="$t('smm.etsy.url_placeholder')"
            class="w-full border rounded-lg p-2 mt-1"
            @keyup.enter="parse"
          />
        </div>

        <div v-if="mode === 'html'" class="space-y-2">
          <label class="text-sm text-gray-600">{{ $t('smm.etsy.html_label') }}</label>
          <p class="text-xs text-gray-500">
            {{ $t('smm.etsy.html_hint') }}
          </p>
          <textarea
            v-model="html"
            rows="8"
            :placeholder="$t('smm.etsy.html_placeholder')"
            class="w-full border rounded-lg p-2 font-mono text-xs"
          />
        </div>

        <p v-if="error" class="text-sm text-red-600 whitespace-pre-line">{{ error }}</p>
        <div class="flex justify-end gap-2 pt-2">
          <button type="button" class="px-4 py-2 text-gray-600" @click="$emit('close')">{{ $t('common.cancel') }}</button>
          <button
            type="button"
            class="px-4 py-2 bg-orange-600 text-white rounded-lg disabled:opacity-50"
            :disabled="parsing || !url.trim() || (mode === 'html' && html.length < 100)"
            @click="parse"
          >
            {{ parsing ? $t('common.loading') : $t('smm.etsy.parse') }}
          </button>
        </div>
      </div>

      <div v-else class="space-y-3">
        <div>
          <label class="text-sm text-gray-600">{{ $t('smm.media.shop') }}</label>
          <select v-model="shopId" required class="w-full border rounded-lg p-2 mt-1">
            <option value="">{{ $t('common.select_placeholder') }}</option>
            <option
              v-for="shop in shops"
              :key="shop.id"
              :value="String(shop.id)"
            >{{ shop.name }}</option>
          </select>
        </div>
        <div>
          <label class="text-sm text-gray-600">{{ $t('common.name') }}</label>
          <input v-model="preview.title" class="w-full border rounded-lg p-2 mt-1" />
        </div>
        <div>
          <label class="text-sm text-gray-600">{{ $t('common.link') }}</label>
          <input v-model="preview.url" type="url" readonly class="w-full border rounded-lg p-2 mt-1 bg-gray-50" />
        </div>
        <div>
          <label class="text-sm text-gray-600">{{ $t('common.description') }}</label>
          <textarea
            v-model="preview.description"
            rows="5"
            class="w-full border rounded-lg p-2 mt-1 resize-y min-h-[7rem]"
          />
        </div>

        <div v-if="preview.images?.length" class="space-y-2">
          <label class="text-sm text-gray-600">{{ $t('smm.etsy.images_count', { n: preview.images.length }) }}</label>
          <div class="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
            <div
              v-for="(img, i) in preview.images"
              :key="'img-'+i"
              class="aspect-square rounded-lg overflow-hidden border bg-gray-100"
            >
              <img :src="img" class="w-full h-full object-cover" :alt="$t('smm.etsy.image_alt', { n: i + 1 })" />
            </div>
          </div>
        </div>

        <div v-if="preview.videos?.length" class="space-y-2">
          <label class="text-sm text-gray-600">{{ $t('smm.etsy.videos_count', { n: preview.videos.length }) }}</label>
          <ul class="text-xs text-gray-600 space-y-1 break-all">
            <li v-for="(video, i) in preview.videos" :key="'vid-'+i">{{ video }}</li>
          </ul>
        </div>

        <p v-if="error" class="text-sm text-red-600">{{ error }}</p>
        <div class="flex justify-between gap-2 pt-2">
          <button type="button" class="px-4 py-2 text-gray-600" @click="resetPreview">{{ $t('common.back') }}</button>
          <div class="flex gap-2">
            <button type="button" class="px-4 py-2 text-gray-600" @click="$emit('close')">{{ $t('common.cancel') }}</button>
            <button
              type="button"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
              :disabled="saving"
              @click="save"
            >
              {{ saving ? $t('common.saving') : $t('common.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    show: { type: Boolean, default: false },
    shops: { type: Array, default: () => [] },
  },
  emits: ['close', 'saved'],
  data: () => ({
    mode: 'url',
    url: '',
    html: '',
    shopId: '',
    preview: null,
    parsing: false,
    saving: false,
    error: null,
  }),
  watch: {
    show(value) {
      if (value && !this.shopId && this.shops.length) {
        this.shopId = this.shops[0]?.id != null ? String(this.shops[0].id) : ''
      }
      if (!value) {
        this.resetAll()
      }
    },
  },
  methods: {
    resetAll() {
      this.mode = 'url'
      this.url = ''
      this.html = ''
      this.shopId = this.shops[0]?.id != null ? String(this.shops[0].id) : ''
      this.preview = null
      this.error = null
      this.parsing = false
      this.saving = false
    },
    resetPreview() {
      this.preview = null
      this.error = null
    },
    formatError(e) {
      const data = e.response?.data
      if (data?.message) return data.message
      if (data?.errors) return Object.values(data.errors).flat().join('\n')
      return this.$t('common.error')
    },
    async parse() {
      if (!this.url.trim()) return
      this.parsing = true
      this.error = null
      try {
        let data
        if (this.mode === 'html') {
          const res = await axios.post('/api/v1/smm/media-contents/parse-etsy-html', {
            url: this.url.trim(),
            html: this.html,
          })
          data = res.data
        } else {
          const res = await axios.post('/api/v1/smm/media-contents/parse-etsy', {
            url: this.url.trim(),
          })
          data = res.data
        }
        this.preview = {
          ...data.data,
          description: data.data.description || '',
        }
      } catch (e) {
        this.error = this.formatError(e)
        if (this.mode === 'url' && this.formatError(e)?.includes('403')) {
          this.error += `\n\n${this.$t('smm.etsy.api_key_hint')}`
          this.mode = 'html'
        }
      } finally {
        this.parsing = false
      }
    },
    async save() {
      if (!this.shopId) {
        this.error = this.$t('smm.media.shop_required')
        return
      }
      if (!this.preview?.images?.length) {
        this.error = this.$t('smm.etsy.no_images')
        return
      }
      this.saving = true
      this.error = null
      try {
        await axios.post('/api/v1/smm/media-contents/import-etsy', {
          shop_id: this.shopId,
          url: this.preview.url,
          title: this.preview.title,
          description: this.preview.description || null,
          images: this.preview.images,
          videos: this.preview.videos || [],
        })
        this.$emit('saved')
        this.$emit('close')
      } catch (e) {
        this.error = this.formatError(e)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
